import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MAP_W, project } from "../data/worldMap";
import { COUNTRIES } from "../data/countries";
import { SEV_RANK, attackLabel, fmtTime, sevLabel, sevVar, timeAgo } from "../lib/format";
import { useFocusTrap, useNow, useScrollLock } from "../lib/hooks";
import { Badge, Button, CountryTag, Empty, IconButton, LiveDot, Segmented, SevBadge } from "./ui";
import { Icon } from "./Icon";

// Defended site the arcs point to (Abu Dhabi)
export const HQ = { lat: 24.4539, lon: 54.3773, name: "Abu Dhabi" };

// Crop to the inhabited latitudes so the map uses its space well
const TOP = project(78, 0)[1];
const BOTTOM = project(-57, 0)[1];
const VIEW = `0 ${TOP.toFixed(1)} ${MAP_W} ${(BOTTOM - TOP).toFixed(1)}`;
const VIEW_H = BOTTOM - TOP;

// Country names as the backend reports them -> map label
const ALIAS = { usa: "united states", us: "united states", "united states of america": "united states", uk: "united kingdom", "russian federation": "russia", "korea, republic of": "south korea", "republic of korea": "south korea", "iran, islamic republic of": "iran", "viet nam": "vietnam", "czech republic": "czechia", "democratic republic of the congo": "dr congo" };
const key = (n) => {
  const k = String(n || "").trim().toLowerCase();
  return ALIAS[k] || k;
};
// Label the larger countries; smaller ones get a label only when attacks come from them
const LABEL_MIN_AREA = 640;
const labelSize = (a) => (a > 5000 ? 14 : a > 1800 ? 12 : 10);

const [HQX, HQY] = project(HQ.lat, HQ.lon);

function arcPath(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const d = Math.hypot(x2 - x1, y2 - y1);
  const lift = Math.min(140, d * 0.32);
  return `M${x1.toFixed(1)},${y1.toFixed(1)} Q${mx.toFixed(1)},${(my - lift).toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
}

/** Group live detections by source, keeping the worst severity and latest time. */
export function aggregateSources(threats, limit = 80) {
  const map = new Map();
  for (const t of threats || []) {
    if (t.is_threat === false || !t.source_ip) continue;
    const lat = Number(t.lat);
    const lon = Number(t.lon);
    if (!lat && !lon) continue;
    const cur = map.get(t.source_ip);
    if (!cur) {
      map.set(t.source_ip, {
        ip: t.source_ip,
        lat,
        lon,
        country: t.country,
        code: t.country_code,
        city: t.city,
        count: 1,
        severity: t.severity || "INFO",
        last: t.timestamp,
        type: t.attack_type,
        contained: !!t.contained,
      });
    } else {
      cur.count += 1;
      if ((SEV_RANK[t.severity] ?? 0) > (SEV_RANK[cur.severity] ?? 0)) cur.severity = t.severity;
      cur.contained = cur.contained || !!t.contained;
    }
    if (map.size >= limit && !cur) break;
  }
  return [...map.values()];
}

const LEGEND = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

export function WorldMap({ sources, mode = "sources", onSelect, arcs = 10, pulses = 4, illustrative, ariaLabel, labels = true }) {
  const [hover, setHover] = useState(null);

  const pts = useMemo(
    () =>
      (sources || []).map((s, i) => {
        const [x, y] = project(s.lat, s.lon);
        return { ...s, x, y, i };
      }),
    [sources],
  );

  // Countries with at least one attack source are shaded and always labelled
  const active = useMemo(() => {
    const m = new Map();
    pts.forEach((p) => {
      const k = key(p.country);
      if (k) m.set(k, (m.get(k) || 0) + (p.count || 1));
    });
    return m;
  }, [pts]);

  const tip = hover != null ? pts.find((p) => p.ip === hover) : null;
  const r = (p) => Math.min(7, 3 + Math.log2(1 + p.count));

  return (
    <div className="geo" onMouseLeave={() => setHover(null)}>
      <svg viewBox={VIEW} role="img" aria-label={ariaLabel || `World map of ${pts.length} attack sources`}>
        <g className="geo-countries">
          {COUNTRIES.map((c) => (
            <path key={c.n} d={c.d} className={active.has(key(c.n)) ? "geo-country is-active" : "geo-country"}>
              <title>{active.has(key(c.n)) ? `${c.n}: ${active.get(key(c.n))} detections` : c.n}</title>
            </path>
          ))}
        </g>
        {labels && (
          <g className="geo-labels" aria-hidden="true">
            {COUNTRIES.filter((c) => c.a >= LABEL_MIN_AREA || active.has(key(c.n))).map((c) => (
              <text key={c.n} x={c.x} y={c.y} className={active.has(key(c.n)) ? "is-active" : undefined} style={{ fontSize: active.has(key(c.n)) ? Math.max(11, labelSize(c.a)) : labelSize(c.a) }}>
                {c.n}
              </text>
            ))}
          </g>
        )}

        {mode === "density" &&
          pts.map((p) => (
            <circle key={`d${p.ip}`} cx={p.x} cy={p.y} r={10 + Math.min(26, p.count * 3)} style={{ fill: sevVar(p.severity), opacity: 0.16 }} />
          ))}

        {mode === "sources" &&
          pts.slice(0, arcs).map((p) => (
            <path key={`a${p.ip}`} d={arcPath(p.x, p.y, HQX, HQY)} className="geo-arc" style={{ stroke: sevVar(p.severity) }} />
          ))}

        {/* defended site */}
        <g aria-hidden="true">
          <circle cx={HQX} cy={HQY} r="5" className="geo-hq-ring" />
          <circle cx={HQX} cy={HQY} r="4" style={{ fill: "var(--brand)", stroke: "var(--panel)" }} strokeWidth="1.5" />
        </g>

        {pts.map((p) => (
          <g
            key={p.ip}
            className="geo-marker"
            tabIndex={onSelect ? 0 : -1}
            role={onSelect ? "button" : undefined}
            aria-label={`${p.ip}, ${p.country || "unknown country"}, ${p.count} detections, worst severity ${sevLabel(p.severity)}`}
            onMouseEnter={() => setHover(p.ip)}
            onFocus={() => setHover(p.ip)}
            onBlur={() => setHover(null)}
            onClick={() => onSelect?.(p.ip)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && onSelect) {
                e.preventDefault();
                onSelect(p.ip);
              }
            }}
          >
            {p.i < pulses && <circle cx={p.x} cy={p.y} r={r(p)} className="geo-pulse" style={{ stroke: sevVar(p.severity) }} />}
            <circle cx={p.x} cy={p.y} r={r(p) + 6} style={{ fill: "transparent" }} />
            <circle cx={p.x} cy={p.y} r={r(p)} style={{ fill: sevVar(p.severity), stroke: "var(--inset)" }} strokeWidth="1.5" />
          </g>
        ))}
      </svg>

      {tip && (
        <div className="geo-tip" style={{ left: `${(tip.x / MAP_W) * 100}%`, top: `${((tip.y - TOP) / VIEW_H) * 100}%` }}>
          <div className="viz-tip">
            <div className="viz-tip-title">
              {[tip.city, tip.country].filter(Boolean).join(", ") || "Unknown location"}
            </div>
            <div className="viz-tip-row">
              <span className="mono">{tip.ip}</span>
              <b>{tip.count}×</b>
            </div>
            <div className="viz-tip-row">
              <span>
                <i style={{ background: sevVar(tip.severity), borderRadius: 4 }} />
                {sevLabel(tip.severity)} · {attackLabel(tip.type)}
              </span>
            </div>
            {!illustrative && (
              <div className="viz-tip-row">
                <span>{tip.contained ? "Blocked by policy" : "Not blocked"}</span>
                <b>{fmtTime(tip.last, false)}</b>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="geo-legend" aria-hidden="true">
        {LEGEND.map((s) => (
          <span key={s}>
            <i style={{ background: sevVar(s) }} />
            {sevLabel(s)}
          </span>
        ))}
        <span>
          <i style={{ background: "var(--brand)" }} />
          {illustrative ? "Defended site" : `Defended site (${HQ.name})`}
        </span>
      </div>
    </div>
  );
}

/** Map card body used on the Overview: live sources with a mode switch. */
export function LiveMap({ threats, onSelect, onExpand }) {
  const [mode, setMode] = useState("sources");
  const sources = useMemo(() => aggregateSources(threats), [threats]);
  return (
    <>
      <div className="row-between" style={{ marginBottom: 12 }}>
        <Segmented
          size="sm"
          label="Map mode"
          value={mode}
          onChange={setMode}
          options={[
            { id: "sources", label: "Sources" },
            { id: "density", label: "Density" },
          ]}
        />
        <div className="row">
          <span className="small muted">{sources.length} sources plotted</span>
          {onExpand && <IconButton icon="maximize" label="Open war-room view" onClick={onExpand} />}
        </div>
      </div>
      {sources.length ? (
        <WorldMap sources={sources} mode={mode} onSelect={onSelect} />
      ) : (
        <div className="geo" style={{ display: "grid", placeItems: "center", minHeight: 260 }}>
          <Empty icon="globe" compact title="No located sources yet">
            Sources appear here as soon as detections with a known location arrive.
          </Empty>
        </div>
      )}
    </>
  );
}

/** Full-screen war-room view: map plus the latest detections. */
export function WarRoom({ open, onClose, threats, onSelect, connected }) {
  const ref = React.useRef(null);
  useFocusTrap(ref, open, onClose);
  useScrollLock(open);
  const now = useNow(5000);
  const sources = useMemo(() => aggregateSources(threats, 120), [threats]);
  const latest = useMemo(() => (threats || []).filter((t) => t.is_threat !== false).slice(0, 14), [threats]);

  useEffect(() => {
    if (!open) return undefined;
    const el = document.documentElement;
    return () => {
      if (document.fullscreenElement === el) document.exitFullscreen?.().catch(() => {});
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div ref={ref} className="geo-full" role="dialog" aria-modal="true" aria-label="War-room map" tabIndex={-1}>
      <div className="geo-full-head">
        <Icon name="globe" size={18} />
        <strong style={{ fontSize: 15 }}>Live attack map</strong>
        <Badge tone={connected ? "ok" : "medium"} dot>
          {connected ? "Live" : "Reconnecting"}
        </Badge>
        <span className="small muted">{sources.length} sources · arcs show the latest detections against the defended site</span>
        <div className="tb-spacer" />
        <Button
          size="sm"
          variant="ghost"
          icon="maximize"
          onClick={() => {
            const el = document.documentElement;
            if (document.fullscreenElement) document.exitFullscreen?.();
            else el.requestFullscreen?.().catch(() => {});
          }}
        >
          Full screen
        </Button>
        <IconButton icon="x" label="Close war-room view" onClick={onClose} />
      </div>
      <div className="geo-full-body">
        <WorldMap sources={sources} onSelect={onSelect} arcs={16} pulses={6} />
        <div className="geo-full-side">
          <div className="eyebrow">Latest detections</div>
          {latest.length === 0 && <Empty compact icon="radar">Waiting for detections.</Empty>}
          {latest.map((t, i) => (
            <button
              key={`${t.source_ip}-${t.timestamp}-${i}`}
              type="button"
              className="card"
              style={{ padding: 12, textAlign: "left", gap: 6, color: "inherit" }}
              onClick={() => onSelect(t.source_ip)}
            >
              <div className="row-between">
                <strong style={{ fontSize: 13 }}>{attackLabel(t.attack_type)}</strong>
                <SevBadge sev={t.severity} />
              </div>
              <div className="row-between small muted">
                <span className="row">
                  <span className="mono">{t.source_ip}</span>
                  <CountryTag code={t.country_code} />
                </span>
                <span>{timeAgo(t.timestamp, now)}</span>
              </div>
              {t.contained && (
                <span className="small" style={{ color: "var(--ok)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <LiveDot tone="ok" pulse={false} /> Blocked by policy
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
